import { css, html, htmlArray } from "../helpers/webComponents";
import { PlayerService } from "../services/PlayerService";
import { PlayerSession } from "@adventure-game/api/src/game-implementation/types";
import { CanvasComponent } from "./CanvasComponent";

/** CSS affecting the {@link KeyboardComponent} */
const styles: string = css`
    @font-face {
        font-family: 'Pixel';
        src: url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
    }
    
    :host {
        width: 100%;
        max-width: 1024px;
        height: 100%;
        z-index: 99;
        display: flex;
        justify-content: center;
        align-items: center;
        --fingerprint-pattern: url('/assets/img/fingerprint_texture.png');
        position: relative;
    }
    
    .uv-flashlight {
        position: fixed;
        width: 150px;
        height: 150px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(128, 0, 255, 0.8) 0%, rgba(128, 0, 255, 0.4) 70%, transparent 100%);
        pointer-events: none;
        z-index: 1000;
        mix-blend-mode: screen;
        filter: blur(5px);
        transform: translate(-50%, -50%);
    }
    
    .keyboard {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 20px;
        background-color: #D4D0C8;
        border: 4px solid #A0A0A0;
        box-shadow: inset 2px 2px 0px #FFFFFF, inset -2px -2px 0px #808080;
        position: relative;
        border-radius: 0px;
    }
    
    .keyboard-row {
        display: flex;
        gap: 8px;
        justify-content: center;
    }
    
    .key {
        width: 60px;
        height: 60px;
        background-color: #ECECEC;
        user-select: none;
        border: 2px solid #A0A0A0;
        border-radius: 0px;
        box-shadow: inset -1px -2px 0 #808080, inset 1px 1px 0 #FFFFFF, 2px 2px 0 #808080;
        display: flex;
        justify-content: center;
        align-items: center;
        font-family: 'Courier New', monospace;
        font-size: 14px;
        color: #000000;
        position: relative;
        overflow: hidden;
        text-shadow: none;
        transition: none;
    }
    
    //.key:active {
    //    transform: none;
    //    box-shadow: inset 1px 1px 0 #808080, inset -1px -1px 0 #FFFFFF;
    //    background-color: #D4D0C8;
    //}
    
    .fingerprint {
        position: absolute;
        width: 100px;
        height: 100px;
        background: var(--fingerprint-pattern);
        background-size: 300px;
        pointer-events: none;
        border-radius: 50%;
        opacity: 0.1; /* Initially less visible */
        mix-blend-mode: multiply;
        transform-origin: center;
        background-attachment: fixed;
        transition: opacity 0.2s ease; /* Smooth transition for opacity changes */
        mask-image: radial-gradient(circle, black 0%, black 50%, transparent 100%);
        -webkit-mask-image: radial-gradient(circle, black 0%, black 50%, transparent 100%);
    }
    
    /* Special keys styling */
    
    .key-esc, .key-tab, .key-backslash {
        width: 80px;
    }
    
    .key-backspace, .key-caps, .key-enter {
        width: 100px;
    }
    
    .key-shift {
        width: 120px;
    }
    
    .key-ctrl, .key-alt {
        width: 70px;
    }
    
    /* Old school keyboards didn't have Win, Fn, or Menu keys */
    
    .key-win, .key-fn, .key-menu {
        width: 70px;
        //background-color: #D4D0C8;
        //color: #808080;
    }
    
    .key-space {
        width: 240px;
        //background-color: #ECECEC;
    }
    
    .close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 30px;
        height: 30px;
        background-color: #D4D0C8;
        border: 2px solid #A0A0A0;
        border-radius: 0px;
        box-shadow: inset -1px -2px 0 #808080, inset 1px 1px 0 #FFFFFF, 2px 2px 0 #808080;
        display: flex;
        justify-content: center;
        align-items: center;
        font-family: 'Courier New', monospace;
        font-size: 18px;
        color: #000000;
        cursor: pointer;
        user-select: none;
    }
    
    .close-button:hover {
        background-color: #ECECEC;
    }
`;

type StringNumberPair = [string, number];

/**
 * Represents the Keyboard component
 *
 * TODO: use actual code
 * TODO: add dirt/dust to most keys to make it look more realistic
 */
export class KeyboardComponent extends HTMLElement {
    /** Keys with fingerprints */
    private _keysWithFingerprints: StringNumberPair[] = [
        ["0", 90], ["1", 100], ["2", 90], ["9", 100],
        ["Space", 75], ["Enter", 76], ["Shift", 65], ["Backspace", 80], ["Caps", 64], ["Win", 60],
    ];

    /** UV flashlight element */
    private _uvFlashlight: HTMLElement | null = null;

    private readonly _playerService: PlayerService = new PlayerService();

    /** Flag indicating if the player has the UV flashlight */
    private _hasUvFlashlight: boolean = false;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.checkForUvFlashlight();
    }

    /**
     * Check if the player has the UV flashlight
     */
    private async checkForUvFlashlight(): Promise<void> {
        try {
            const response: Response = await fetch(`${VITE_API_URL}game/session`, {
                method: "GET",
                headers: {
                    "X-PlayerSessionId": this._playerService.getPlayerSessionId(),
                },
            });
            const data: PlayerSession = await response.json() as PlayerSession;

            // this should be a UvFlashlightItem.Alias, but that causes errors
            this._hasUvFlashlight = data.inventory.includes("uv-flashlight");
        }
        catch (error) {
            console.error("Error checking for UV flashlight:", error);
        }

        this.render();

        document.addEventListener("mousemove", this.handleMouseMove);
    }

    /**
     * Clean up event listeners when component is removed
     */
    public disconnectedCallback(): void {
        document.removeEventListener("mousemove", this.handleMouseMove);
    }

    /**
     * Handle mouse movement to update fingerprint positions and move UV flashlight
     */
    private handleMouseMove = (e: MouseEvent): void => {
        if (!this.shadowRoot) {
            return;
        }

        // Only process UV flashlight and fingerprints if the player has the UV flashlight
        if (!this._hasUvFlashlight) {
            return;
        }

        // Move UV flashlight with cursor
        if (this._uvFlashlight) {
            this._uvFlashlight.style.left = `${e.clientX}px`;
            this._uvFlashlight.style.top = `${e.clientY}px`;
        }

        const fingerprints: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll(".fingerprint");

        fingerprints.forEach(fingerprint => {
            const key: HTMLElement | null = fingerprint.parentElement;

            if (!key) return;

            // Get the key's position relative to the viewport
            const keyRect: DOMRect = key.getBoundingClientRect();

            // Calculate position relative to the key element
            const relativeX: number = e.clientX - keyRect.left - 50;
            const relativeY: number = e.clientY - keyRect.top - 50;

            fingerprint.style.left = `${relativeX}px`;
            fingerprint.style.top = `${relativeY}px`;

            // // Calculate distance from mouse to fingerprint center
            // const distance = Math.sqrt(
            //     Math.pow(e.clientX - (keyRect.left + parseFloat(fingerprint.style.left) + 50), 2) +
            //     Math.pow(e.clientY - (keyRect.top + parseFloat(fingerprint.style.top) + 50), 2)
            // );
            //
            // // Make fingerprints more visible when under UV light (closer to mouse)
            // const visibility: number = Math.max(0.1, Math.min(1, 1 - distance / 200));
            // fingerprint.style.opacity = visibility.toString();
        });
    };

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        // Create UV flashlight element only if the player has the UV flashlight
        this._uvFlashlight = this._hasUvFlashlight
            ? html`
                <div class="uv-flashlight"></div>`
            : null;

        // Create close button
        const closeButton: HTMLElement = html`
            <div class="close-button">X</div>
        `;

        // Add click event listener to close button
        closeButton.addEventListener("click", () => {
            CanvasComponent.closeFromSubcomponent();
        });

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderKeyboard()}
            ${this._uvFlashlight || ""}
            ${closeButton}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Keyboard layout configuration for an old-school style keyboard
     * (maintaining 60% layout for compatibility)
     */
    private readonly _keyboardLayout: string[][] = [
        ["Esc", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "=", "Backspace"],
        ["Tab", "Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P", "[", "]", "\\"],
        ["Caps", "A", "S", "D", "F", "G", "H", "J", "K", "L", ";", "'", "Enter"],
        ["Shift", "Z", "X", "C", "V", "B", "N", "M", ",", ".", "/", "Shift"],
        ["Ctrl", "Win", "Alt", "Space", "Alt", "Fn", "Menu", "Ctrl"],
    ];

    /**
     * Create a key element with optional fingerprint
     *
     * @param key - The key label
     * @returns HTML element for the key
     */
    private createKeyElement(key: string): HTMLElement {
        // Find the fingerprint data for this key if it exists
        const fingerprintData: StringNumberPair | undefined = this._keysWithFingerprints.find(([k]) => k === key);

        const randomNumber: number = Math.floor(Math.random() * (45 - 30) + 30);

        // Create fingerprint element if the player has the UV flashlight
        const fingerprintElement: HTMLElement | null = (this._hasUvFlashlight)
            ? html`
                <div class="fingerprint" style="opacity: ${fingerprintData ? fingerprintData[1] / 100 : randomNumber / 100}; left: -9999px; top: -9999px;"></div>`
            : null;

        // Determine the appropriate class based on the key type
        let className: string = "key";

        // Add specific class for special keys
        switch (key) {
            case "Esc":
                className += " key-esc";
                break;
            case "Tab":
                className += " key-tab";
                break;
            case "\\":
                className += " key-backslash";
                break;
            case "Backspace":
                className += " key-backspace";
                break;
            case "Caps":
                className += " key-caps";
                break;
            case "Enter":
                className += " key-enter";
                break;
            case "Shift":
                className += " key-shift";
                break;
            case "Ctrl":
            case "Alt":
                className += " key-" + key.toLowerCase();
                break;
            case "Win":
                className += " key-win";
                break;
            case "Fn":
                className += " key-fn";
                break;
            case "Menu":
                className += " key-menu";
                break;
            case "Space":
                className += " key-space";
                break;
        }

        return html`
            <div class="${className}">
                ${fingerprintElement?.outerHTML || ""}
                ${key === "Space" ? "" : key}
            </div>
        `;
    }

    /**
     * Render the keyboard element
     *
     * @returns HTML element for the keyboard
     */
    private renderKeyboard(): HTMLElement {
        const keyboard: HTMLElement = html`
            <div class="keyboard"></div>`;

        // Create each row of the keyboard
        this._keyboardLayout.forEach(rowKeys => {
            const row: HTMLElement = html`
                <div class="keyboard-row"></div>`;

            // Create each key in the row
            rowKeys.forEach(key => {
                const keyElement: HTMLElement = this.createKeyElement(key);
                row.appendChild(keyElement);
            });

            keyboard.appendChild(row);
        });

        return keyboard;
    }
}
